/**
 * data-gb-* 속성 기반 자동 트래킹.
 *
 * 지원:
 *   data-gb-track="click|hover|view|submit"
 *   data-gb-event="<canonical_event_name>"
 *   data-gb-props='{"key":"value"}'             (JSON 문자열)
 *   data-gb-only="ga4,amplitude"                 (쉼표 구분)
 *   data-gb-exclude="clarity"
 *   data-gb-view-threshold="0.5"                 (view 전용, IntersectionObserver threshold)
 *   data-gb-view-margin="0px 0px -100px 0px"     (view 전용, IntersectionObserver rootMargin — 예: 화면 하단에서 100px 올라왔을 때 발사)
 *   data-gb-view-mode="repeat"                   (view 전용, 매 진입마다 발사 + visibility/focus 게이트 + debounce)
 *
 * 행위별 이벤트 분리 (동일 element에 view+click 동시):
 *   data-gb-event-view  / data-gb-props-view
 *   data-gb-event-click / data-gb-props-click
 *   (없으면 data-gb-event / data-gb-props 로 fallback)
 *
 * 보강 훅:
 *   window.GBTrackEnrich(event, props, el) — 발사 직전 props 보강 (예: section_display_order 자동 주입)
 */
(function () {
  'use strict';
  if (!window.GBTrack) { console.warn('[GBTrack:dom] core not loaded'); return; }

  var REPEAT_DEBOUNCE_MS = 500;

  var parseFailedOnce = new WeakSet();
  var observedEls = new WeakSet();

  function parseRawProps(el, raw) {
    if (!raw) return {};
    try { return JSON.parse(raw); }
    catch (e) {
      if (!parseFailedOnce.has(el)) {
        console.warn('[GBTrack:dom] data-gb-props JSON 파싱 실패', el, raw);
        parseFailedOnce.add(el);
      }
      return {};
    }
  }

  function parseList(el, attr) {
    var raw = el.getAttribute(attr);
    if (!raw) return null;
    var arr = raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    return arr.length ? arr : null;
  }

  function fire(el, mode /* "view" | "click" | "submit" | "hover" */) {
    var evtAttr   = mode ? 'data-gb-event-' + mode : null;
    var propsAttr = mode ? 'data-gb-props-' + mode : null;
    var event = (evtAttr && el.getAttribute(evtAttr)) || el.getAttribute('data-gb-event');
    if (!event) {
      console.warn('[GBTrack:dom] data-gb-event 누락', el);
      return;
    }
    var rawProps = (propsAttr && el.getAttribute(propsAttr)) || el.getAttribute('data-gb-props');
    var props = parseRawProps(el, rawProps);

    if (typeof window.GBTrackEnrich === 'function') {
      try { window.GBTrackEnrich(event, props, el); }
      catch (err) { console.warn('[GBTrack:dom] GBTrackEnrich 실패', err); }
    }

    var opts = {};
    var only = parseList(el, 'data-gb-only');
    var exclude = parseList(el, 'data-gb-exclude');
    if (only) opts.only = only;
    if (exclude) opts.exclude = exclude;
    window.GBTrack.track(event, props, opts);
  }

  // click: 캡처 페이즈에 부착 — 컴포넌트가 stopPropagation 으로 버블을 막아도 트래킹은 발사되도록.
  document.addEventListener('click', function (e) {
    var el = e.target.closest && e.target.closest('[data-gb-track~="click"]');
    if (el) fire(el, 'click');
  }, true);

  // submit
  document.addEventListener('submit', function (e) {
    var el = e.target.closest && e.target.closest('[data-gb-track~="submit"]');
    if (el) fire(el, 'submit');
  }, true);

  // hover: mouseenter bubble 불가 → mouseover 위임 + relatedTarget 체크로 대체.
  document.addEventListener('mouseover', function (e) {
    var el = e.target.closest && e.target.closest('[data-gb-track~="hover"]');
    if (!el) return;
    if (el.contains(e.relatedTarget)) return;
    fire(el, 'hover');
  }, true);

  // view: IntersectionObserver, 최초 진입 1회 (기본)
  var viewObserver = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          fire(entry.target, 'view');
          viewObserver.unobserve(entry.target);
        });
      }, { threshold: 0.3 })
    : null;

  // view-mode="repeat": 매 진입마다 발사 + visibility/focus 게이트 + debounce
  var repeatVisible  = new WeakSet();             // 현재 viewport 안에 있는지
  var repeatLastFire = new WeakMap();             // el → 마지막 발사 timestamp
  var repeatPending  = new WeakSet();             // 보류 등록 여부 (중복 방지용)
  var repeatPendingList = [];                     // 실제 보류 element 리스트 (WeakSet은 iterate 불가)

  function pageActive() {
    var vis = (typeof document.visibilityState === 'string') ? document.visibilityState === 'visible' : true;
    var focus = (typeof document.hasFocus === 'function') ? document.hasFocus() : true;
    return vis && focus;
  }

  function tryFireRepeat(el) {
    if (!repeatVisible.has(el)) return;
    if (!pageActive()) {
      if (!repeatPending.has(el)) {
        repeatPending.add(el);
        repeatPendingList.push(el);
      }
      return;
    }
    var now = Date.now();
    var last = repeatLastFire.get(el) || 0;
    if (now - last < REPEAT_DEBOUNCE_MS) return;
    repeatLastFire.set(el, now);
    fire(el, 'view');
  }

  var repeatObserver = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            repeatVisible.add(entry.target);
            tryFireRepeat(entry.target);
          } else {
            repeatVisible.delete(entry.target);
          }
        });
      }, { threshold: 0.3 })
    : null;

  function flushPending() {
    if (!pageActive()) return;
    var list = repeatPendingList;
    repeatPendingList = [];
    list.forEach(function (el) {
      repeatPending.delete(el);
      tryFireRepeat(el);
    });
  }

  document.addEventListener('visibilitychange', flushPending);
  window.addEventListener('focus', flushPending);

  function scanViewTargets(root) {
    if (!viewObserver) return;
    (root || document).querySelectorAll('[data-gb-track~="view"]').forEach(function (el) {
      if (observedEls.has(el)) return;  // 중복 관찰 방지
      observedEls.add(el);

      // 매번 발사 모드
      if (el.getAttribute('data-gb-view-mode') === 'repeat') {
        if (repeatObserver) repeatObserver.observe(el);
        return;
      }

      var th = parseFloat(el.getAttribute('data-gb-view-threshold') || 'NaN');
      var viewMargin = el.getAttribute('data-gb-view-margin'); // IntersectionObserver rootMargin
      if (!isNaN(th) || viewMargin) {
        // 커스텀 threshold/rootMargin: 기본 옵저버를 타지 않고 처음부터 localObs 만 사용.
        var minRatio = isNaN(th) ? 0 : th;
        var ioOpts = { threshold: isNaN(th) ? 0 : [th, Math.min(th + 0.01, 1)] };
        if (viewMargin) ioOpts.rootMargin = viewMargin;
        var localObs = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            if (entry.intersectionRatio >= minRatio) {
              fire(entry.target, 'view');
              localObs.unobserve(entry.target);
            }
          });
        }, ioOpts);
        localObs.observe(el);
      } else {
        viewObserver.observe(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scanViewTargets(document); });
  } else {
    scanViewTargets(document);
  }

  // 동적으로 추가되는 엘리먼트도 지원
  if ('MutationObserver' in window) {
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        m.addedNodes.forEach(function (n) {
          if (n.nodeType === 1) scanViewTargets(n);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
